/**
 * The ambient background artwork that sits behind the whole application.
 *
 * Fifteen abstract compositions, generated from Norican design-system tokens
 * and the five business-unit brand colours (DISA, Wheelabrator, Simpson,
 * StrikoWestofen, Monitizer), live in `public/brand/backgrounds/`. One is
 * chosen at random per page load so the product feels alive without ever
 * becoming a distraction.
 *
 * Deliberate choices:
 *
 * - The pick happens **once at module scope**, not in render. React StrictMode
 *   double-invokes component bodies in development, and a `Math.random()` in
 *   render would also re-roll on every parent re-render — either would make the
 *   artwork flicker mid-session. Module scope evaluates once per page load,
 *   which is exactly the cadence we want.
 * - `position: fixed` keeps the artwork still while content scrolls over it, so
 *   it reads as a backdrop rather than as part of the page.
 * - Opacity is low by design and drops further on small screens, where artwork
 *   competes with text far more aggressively.
 * - `aria-hidden` + `pointer-events-none`: it is decoration, so it must never
 *   reach the accessibility tree or intercept a click.
 * - It is hidden entirely in print and under `prefers-contrast: more`, where a
 *   wash behind body text is actively harmful to legibility.
 * - The image is selected in CSS off `data-background`, not via an inline
 *   `style`, because the frontend rules forbid inline styles. One rule per
 *   background lives in `src/styles/globals.css`; the two lists must stay in
 *   step, which `site-background.test.mjs` asserts.
 */

/** Every background in `public/brand/backgrounds/`, by filename. */
export const SITE_BACKGROUNDS = [
  "bg-01-molten-flow",
  "bg-02-sand-grain",
  "bg-03-blast-radial",
  "bg-04-blueprint",
  "bg-05-heat-rings",
  "bg-06-contours",
  "bg-07-hex-mesh",
  "bg-08-streamlines",
  "bg-09-strata",
  "bg-10-network",
  "bg-11-isometric",
  "bg-12-spark-arcs",
  "bg-13-interference",
  "bg-14-facets",
  "bg-15-diagonal",
] as const;

export type SiteBackgroundName = (typeof SITE_BACKGROUNDS)[number];

/**
 * Chosen once per page load — see the note on module scope above.
 * Exported so a test can assert it is a member of the set.
 */
export const activeBackground: SiteBackgroundName =
  SITE_BACKGROUNDS[Math.floor(Math.random() * SITE_BACKGROUNDS.length)]!;

export function SiteBackground() {
  return (
    <div aria-hidden="true" className="site-background" data-background={activeBackground}>
      <div className="site-background__art" />
      <div className="site-background__wash" />
    </div>
  );
}
